import {
  CalendarPlus,
  Clock,
  ImageIcon,
  LinkIcon,
  MapPin,
  Tag,
  Trash2,
  Wallet,
} from "lucide-react"
import type { FormEvent } from "react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

export interface Neighborhood {
  id: number
  name: string
  city?: { id: number; name: string } | null
}

export interface EventFormData {
  title: string
  description: string
  category: string
  start_date: string
  end_date: string
  venue_name: string
  address: string
  latitude: string
  longitude: string
  neighborhood_id: string
  city_id: string | number
  price: string
  is_free: boolean
  capacity: string
  visibility: string
  featured?: boolean
  image?: File | null
  image_url?: string
  remove_image?: boolean
}

export interface EventFormProps {
  title: string
  data: EventFormData
  errors: Record<string, string>
  neighborhoods: Neighborhood[]
  categories: { value: string; label: string; emoji?: string }[]
  processing?: boolean
  onChange: (partial: Partial<EventFormData>) => void
  onSubmit: (e: FormEvent) => void
  onCancel: () => void
  submitLabel?: string
  currentImage?: string | null
}

export function EventForm({
  title,
  data,
  errors,
  neighborhoods,
  categories,
  processing = false,
  onChange,
  onSubmit,
  onCancel,
  submitLabel = "Guardar",
  currentImage = null,
}: EventFormProps) {
  const err = errors
  const [imageMode, setImageMode] = useState<"file" | "url">("file")

  const toDatePart = (value: string) => {
    if (!value) return ""
    const d = new Date(value)
    if (isNaN(d.getTime())) return ""
    const pad = (n: number) => n.toString().padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  }

  const toTimePart = (value: string) => {
    if (!value) return ""
    const d = new Date(value)
    if (isNaN(d.getTime())) return ""
    const pad = (n: number) => n.toString().padStart(2, "0")
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const combineDateTime = (date: string, time: string) => {
    if (!date) return ""
    return time ? `${date}T${time}` : `${date}T00:00`
  }

  return (
    <div className="w-full max-w-4xl">
      <CardTitle className="text-foreground flex items-center gap-3 px-4 text-2xl font-bold">
        <CalendarPlus className="text-primary h-8 w-8" />
        {title}
      </CardTitle>
      <CardContent className="px-4">
        {Object.keys(err).length > 0 && (
          <div className="alert-error mb-6">
            <div className="mb-2 flex items-center gap-2">
              <div className="bg-status-error h-2 w-2 rounded-full"></div>
              <p className="text-destructive font-semibold">
                Por favor corrige los siguientes errores:
              </p>
            </div>
            <ul className="text-destructive list-disc space-y-1 pl-6 text-sm">
              {Object.entries(err).map(([k, v]) => (
                <li key={k}>{v}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-4">
            <div className="mt-2">
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Título del Evento
              </Label>
              <Input
                id="title"
                value={data.title}
                onChange={(e) => onChange({ title: e.target.value })}
                required
                className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                placeholder="Ej: Evento de Networking"
              />
              {err.title && (
                <p className="text-destructive mt-1 flex items-center gap-1 text-sm">
                  <div className="bg-status-error h-1 w-1 rounded-full"></div>
                  {err.title}
                </p>
              )}
            </div>

            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Descripción
              </Label>
              <Textarea
                value={data.description}
                onChange={(e) => onChange({ description: e.target.value })}
                rows={4}
                className="border-border focus:border-primary resize-none border-2 text-base transition-colors duration-200"
                placeholder="Describe los detalles del evento..."
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              id="featured"
              type="checkbox"
              checked={Boolean(data.featured)}
              onChange={(e) => onChange({ featured: e.target.checked })}
              className="accent-primary h-4 w-4"
            />
            <Label htmlFor="featured" className="text-sm">
              Marcar como destacado
            </Label>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-primary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <ImageIcon className="text-primary h-5 w-5" />
                Imagen del evento
              </h3>
            </div>

            {currentImage && !data.remove_image && (
              <div className="bg-muted/50 border-muted flex items-center gap-4 rounded-lg border-2 border-dashed p-4">
                <div className="border-border relative h-24 w-40 overflow-hidden rounded-lg border-2">
                  <img
                    src={currentImage || "/placeholder.svg"}
                    alt="Imagen actual"
                    className="h-full w-full object-cover"
                  />
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() =>
                    onChange({ remove_image: true, image: null, image_url: "" })
                  }
                  className="h-10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}

            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Button
                type="button"
                variant={imageMode === "file" ? "default" : "outline"}
                size="sm"
                onClick={() => setImageMode("file")}
                className="h-8 shrink-0 px-4"
              >
                <ImageIcon className="mr-2 h-4 w-4" />
                Subir archivo
              </Button>
              <Button
                type="button"
                variant={imageMode === "url" ? "default" : "outline"}
                size="sm"
                onClick={() => setImageMode("url")}
                className="h-8 shrink-0 px-4"
              >
                <LinkIcon className="mr-2 h-4 w-4" />
                Usar URL
              </Button>
            </div>

            {imageMode === "file" && (
              <div className="w-full max-w-full min-w-0">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null
                    onChange({ image: file })
                  }}
                  className="border-muted file:bg-primary/10 file:text-primary hover:file:bg-primary/20 h-12 w-full border-2 border-dashed file:mr-4 file:truncate file:rounded-lg file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium"
                />
              </div>
            )}

            {imageMode === "url" && (
              <div className="w-full max-w-full min-w-0">
                <Input
                  type="url"
                  placeholder="https://ejemplo.com/imagen.jpg"
                  value={data.image_url ?? ""}
                  onChange={(e) => onChange({ image_url: e.target.value })}
                  className="border-border focus:border-primary h-12 w-full border-2 text-base transition-colors duration-200"
                />
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-secondary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <Tag className="text-secondary h-5 w-5" />
                Categoría y Capacidad
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Categoría
                </Label>
                <Select
                  value={data.category}
                  onValueChange={(value) => onChange({ category: value })}
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        <span className="inline-flex items-center gap-2">
                          <span>{c.emoji ?? ""}</span>
                          <span>{c.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {err.category && (
                  <p className="text-destructive mt-1 text-sm">
                    {err.category}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Capacidad
                </Label>
                <Input
                  type="number"
                  min={1}
                  value={data.capacity}
                  onChange={(e) => onChange({ capacity: e.target.value })}
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                />
              </div>
            </div>
          </div>

          {/* Fechas del Evento */}
          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-accent h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <Clock className="text-primary h-5 w-5" />
                Fechas del Evento
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <Label className="text-muted-foreground text-sm font-medium">
                  Fecha y hora de inicio
                </Label>
                <div className="space-y-3">
                  <Input
                    type="date"
                    value={toDatePart(data.start_date)}
                    onChange={(e) => {
                      const newDate = e.target.value
                      const time = toTimePart(data.start_date)
                      onChange({ start_date: combineDateTime(newDate, time) })
                    }}
                    required
                    className="border-border focus:border-primary h-12 border-2"
                  />
                  <Input
                    type="time"
                    value={toTimePart(data.start_date)}
                    onChange={(e) => {
                      const date = toDatePart(data.start_date)
                      const newTime = e.target.value
                      onChange({ start_date: combineDateTime(date, newTime) })
                    }}
                    required
                    className="border-border focus:border-primary h-12 border-2"
                  />
                </div>
                {err.start_date && (
                  <p className="text-destructive mt-1 text-sm">
                    {err.start_date}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-muted-foreground text-sm font-medium">
                  Fecha y hora de fin (opcional)
                </Label>
                <div className="space-y-3">
                  <Input
                    type="date"
                    value={toDatePart(data.end_date)}
                    onChange={(e) => {
                      const newDate = e.target.value
                      const time = toTimePart(data.end_date)
                      onChange({ end_date: combineDateTime(newDate, time) })
                    }}
                    className="border-border focus:border-primary h-12 border-2"
                  />
                  <Input
                    type="time"
                    value={toTimePart(data.end_date)}
                    onChange={(e) => {
                      const date = toDatePart(data.end_date)
                      const newTime = e.target.value
                      onChange({ end_date: combineDateTime(date, newTime) })
                    }}
                    className="border-border focus:border-primary h-12 border-2"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Ubicación */}
          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-primary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <MapPin className="text-primary h-5 w-5" />
                Ubicación
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Lugar
                </Label>
                <Input
                  value={data.venue_name}
                  onChange={(e) => onChange({ venue_name: e.target.value })}
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                />
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Barrio
                </Label>
                <Select
                  value={data.neighborhood_id}
                  onValueChange={(value) =>
                    onChange({ neighborhood_id: value })
                  }
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue placeholder="Selecciona un barrio" />
                  </SelectTrigger>
                  <SelectContent>
                    {neighborhoods.map((n) => (
                      <SelectItem key={n.id} value={String(n.id)}>
                        {n.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Dirección
                </Label>
                <Input
                  value={data.address}
                  onChange={(e) => onChange({ address: e.target.value })}
                  placeholder="Calle y número"
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                />
              </div>
            </div>
          </div>

          {/* Precio y Visibilidad */}
          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-secondary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <Wallet className="text-secondary h-5 w-5" />
                Precio y Visibilidad
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  ¿Es gratis?
                </Label>
                <Select
                  value={data.is_free ? "true" : "false"}
                  onValueChange={(value) =>
                    onChange({ is_free: value === "true" })
                  }
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Sí</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Visibilidad
                </Label>
                <Select
                  value={data.visibility}
                  onValueChange={(value) => onChange({ visibility: value })}
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue placeholder="Alcance del evento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="city">Ciudad</SelectItem>
                    <SelectItem value="neighborhood">Barrio</SelectItem>
                    <SelectItem value="global">Global</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {!data.is_free && (
                <div>
                  <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                    Precio
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    disabled={data.is_free}
                    value={data.price}
                    onChange={(e) => onChange({ price: e.target.value })}
                    className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="border-border flex justify-end gap-4 border-t pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="h-9 bg-transparent px-4 text-sm font-medium"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="h-9 px-4 text-sm font-medium"
            >
              {submitLabel}
            </Button>
          </div>
        </form>
      </CardContent>
    </div>
  )
}
